<template>
  <v-card id="account-log-lengths-range-form">
    <v-card-title class="primary white--text">
      <v-row>
        <v-col>
          <span>{{ $t('addRange') }}</span>
        </v-col>

        <v-col cols="auto">
          <BaseDialogActions hideRefresh hideFullscreen />
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="pt-5">
      <v-row>
        <v-col cols="12" sm="6">
          <v-text-field v-model="minLogLength"
          id="min-log-length"
          :label="$t('minLogLength')"
          :rules="[minLogLengthRule]">
          </v-text-field>
        </v-col>

        <v-col cols="12" sm="6">
          <v-text-field v-model="maxLogLength"
          id="max-log-length"
          :label="$t('maxLogLength')"
          :rules="[maxLogLengthRule]">
          </v-text-field>
        </v-col>

        <v-col cols="12" v-if="previewVisible">
          <DetailCard :title="$t('lengths')" style="max-height: max(256px, calc(90vh - 264px)); overflow-y: auto;">
            <div id="preview-account-log-lengths-import">
              <v-chip
                :style="{ opacity: lengthAlreadyExists(length) ? 0.5 : 1 }"
                v-for="(length) in previewedLogLengths"
                :key="length"
                label
                :outlined="!lengthAlreadyExists(length)"
                class="grey-chip mr-1 mb-1"
                :ripple="false"
              >
                <v-icon small class="mr-1" v-if="lengthAlreadyExists(length)">mdi-check</v-icon>
                <span>
                  {{ `${parseInt(length)}'` }}
                </span>
              </v-chip>
            </div>
          </DetailCard>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="my-2 px-0">
      <v-row justify="end">
        <v-col cols="auto">
          <v-btn :disabled="saveButtonDisabled" color="primary" @click="onSaveClicked">
            {{  $t('save') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import { MAX_LENGTH, MIN_LENGTH } from './LogLengths'
import { mapActions, mapGetters } from 'vuex'
export default {
  name: 'AccountLogLengthsRangeForm',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue')
  },

  props: {
    accountId: {
      type: Number,
      required: true
    }
  },

  data: () => ({
    minLogLength: '',
    maxLogLength: '',
    previewedLogLengths: []
  }),

  computed: {
    ...mapGetters('account-scaling', ['accountLogLengths']),

    previewVisible () {
      return this.minLogLengthRule(this.minLogLength) === true && this.maxLogLengthRule(this.maxLogLength) === true
    },

    saveButtonDisabled () {
      return this.previewedLogLengths.filter(ll => !this.lengthAlreadyExists(ll)).length < 1
    }
  },

  mounted () {
    this.setPreviewedLogLengths()
    this.$nextTick(() => {
      document.getElementById('min-log-length').focus()
    })
  },

  watch: {
    minLogLength () {
      this.setPreviewedLogLengths()
    },
    maxLogLength () {
      this.setPreviewedLogLengths()
    }
  },

  methods: {
    ...mapActions('account-scaling', ['createAccountLogLengths']),

    async onSave () {
      const response = await this.createAccountLogLengths({
        accountId: this.accountId,
        logLengths: this.previewedLogLengths.filter(ll => !this.lengthAlreadyExists(ll))
      })
      if (response && response?.every(ll => !!ll.logLengthId)) {
        this.$emit('creation-successful')
      }
    },

    minLogLengthRule (minLogLength) {
      const message = this.$t('mustBeBetween', { low: MIN_LENGTH, high: MAX_LENGTH })
      const asInt = parseInt(minLogLength)
      if (isNaN(asInt)) {
        return message
      }
      if (asInt < MIN_LENGTH || asInt > MAX_LENGTH) {
        return message
      }
      return true
    },

    maxLogLengthRule (maxLogLength) {
      const message = this.$t('mustBeBetween', { low: MIN_LENGTH, high: MAX_LENGTH })
      const asInt = parseInt(maxLogLength)
      if (isNaN(asInt)) {
        return message
      }
      if (asInt < MIN_LENGTH || asInt > MAX_LENGTH) {
        return message
      }
      return true
    },

    async onSaveClicked () {
      if (this.previewVisible) await this.onSave()
    },

    lengthAlreadyExists (length) {
      return this.accountLogLengths.some(logLength => logLength.length === parseInt(length))
    },

    setPreviewedLogLengths () {
      if (!this.previewVisible) return []
      const range = this.maxLogLength - this.minLogLength + 1
      this.previewedLogLengths = [...Array(range).keys()].map(i => i + parseInt(this.minLogLength))
    }
  }
}
</script>
